import React, { useEffect, useState } from "react";
import { Upload, FileText, Trash2, Computer } from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Loading from "./Loading";
import Footer from "./Footer";
import { apiUrl } from "../lib/api";

function Hero({ loading, setLoading }) {
  const [text, setText] = useState("");
  const [warning, setWarning] = useState("");
  const [file, setFile] = useState(null);
  const navigate = useNavigate();

  const makeRunId = () => {
    try {
      if (typeof window !== "undefined" && window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
      }
    } catch { }
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  };

  function handleInputChange(event) {
    const cleanedValue = event.target.value.trim(); // removes spaces
    setText(cleanedValue);
    if (warning) setWarning("");
  }

  function handleFileChange(event) {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      const allowedExtensions = /\.(fna|fasta|gbff)$/i;
      if (!allowedExtensions.test(selectedFile.name)) {
        setWarning(" Only .fna and .gbff files are allowed");
        setFile(null);
        event.target.value = "";
        return;
      }
      setFile(selectedFile);
      setWarning("");
    }
  }

  async function handleClickProcessGenome() {
    if (!file && !text.trim()) {
      setWarning(" Please provide either a file or a RefSeq ID");
      return;
    }

    if (!file) {
      if (!/^[a-zA-Z0-9_.-]*$/.test(text)) {
        setWarning(" Only letters, numbers, hyphens (-)and underscores (_) allowed");
        return;
      }
      if (text[0] !== "G" && text[0] !== "N") {
        setWarning(
          "Please enter a valid RefSeq reference sequence ID or assembly accession"
        );
        return;
      }
    }

    setLoading(true);
    const formData = new FormData();
    let num = makeRunId();
    formData.append("num", num);
    if (file) formData.append("file", file);
    else {
      formData.append("refseq_id", text);
    }
    // console.log("From Hero.jsx:");
    // console.log(num);
    try {
      // Prefer background execution in production; still works with the old sync API.
      // fetch(apiUrl("/run-origami?background=1")
      const response = await fetch(apiUrl("/run-origami"), {
        method: "POST",
        body: formData,
      });
      if (!response.ok) {
        let bodyText = "";
        try {
          bodyText = await response.text();
        } catch { }
        const hint =
          response.status === 413
            ? " Upload too large (server limit)."
            : response.status === 404
              ? " API route not found (proxy/base path misconfigured)."
              : response.status >= 500
                ? " Server error (check backend logs / proxy)."
                : "";
        throw new Error(
          `HTTP ${response.status}.${hint}${bodyText ? ` ${bodyText}` : ""}`
        );
      }
      const data = await response.json();
      console.log("Response:", data);
      // Backward compatible:
      // - New API returns { job_id } and we poll until completion.
      // - Old API returns the final result object directly.
      setLoading(false);
      if ((data?.job_id )) {
        // Important: clear global loading state before leaving /search, otherwise
        // returning to /search will keep showing <Loading /> forever.
        navigate(`/processing/${data.job_id}`);
        return;
      }
      // Important: clear global loading state before leaving /search, otherwise
      // returning to /search will keep showing <Loading /> forever.
      console.log("From the Hero.jsx:");
      console.log(data.job_id);
      navigate("/result", { state: { result: data , job_id:data.job_id} });
    } catch (error) {
      console.error("Error processing genome:", error);
      setWarning(
        `Failed to process genome. Please try again.${error?.message ? ` (${error.message})` : ""}`
      );
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!warning) return;
    const timer = setTimeout(() => setWarning(""), 3000);
    return () => clearTimeout(timer);
  }, [warning]);

  if (loading) return <Loading />;

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 font-sans">
        <motion.section
          className="max-w-4xl mx-auto px-6 py-16"
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-10">
            <div className="mb-10 text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold mb-3">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                Web Server
              </div>
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="p-2.5 rounded-full bg-emerald-100">
                  <Computer className="w-5 h-5 text-emerald-700" />
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-slate-900">
                  Start <span className="text-emerald-700">Genome Analysis</span>
                </h1>
              </div>
              <p className="text-sm md:text-base leading-relaxed text-slate-700 max-w-2xl mx-auto">
                Upload a bacterial genome sequence file (<strong>.fna or .gbff</strong>) or provide an{" "}
                <strong>NCBI accession ID</strong>. Origenomi reorganizes the
                genome so it begins at the{" "}
                <strong>Origin of Replication (OriC)</strong> and helps you
                compare the original and reorganized sequences.
              </p>
            </div>

            <div className="space-y-6">
              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Upload Genome File (max 50mb)
                </label>
                <label className="flex items-center justify-center gap-2 cursor-pointer border-2 border-dashed border-gray-300 rounded-md py-6 text-gray-600 hover:border-blue-500 hover:bg-blue-50 transition">
                  <Upload size={18} />
                  <span className="font-medium">Choose File</span>
                  <input
                    type="file"
                    accept=".fna,.gbff"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </label>

                {file && (
                  <div className="flex items-center gap-2 mt-2 text-sm text-gray-700">
                    <FileText size={16} />
                    <strong>{file.name}</strong>
                    <button
                      onClick={() => setFile(null)}
                      className="ml-2 text-red-600 hover:text-red-700"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Or Enter NCBI Accession ID
                </label>
                <input
                  type="text"
                  value={text}
                  onChange={handleInputChange}
                  placeholder="e.g. GCF_000014205.1_ASM1420v1"
                  className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition text-sm"
                />
              </div>

              {warning && (
                <div className="text-red-600 text-sm font-medium">
                  {warning}
                </div>
              )}

              {/* Buttons */}
              <div className="flex flex-wrap items-center gap-4 pt-4">
                <button
                  onClick={handleClickProcessGenome}
                  className="bg-blue-700 text-white px-5 py-2 rounded-md text-sm font-medium hover:bg-blue-800 transition"
                >
                  Process Genome
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setText("GCF_010731855.1_ASM1073185v1");
                    setWarning("");
                    setFile(null);
                  }}
                  className="text-blue-700 border border-blue-700 px-5 py-2 rounded-md text-sm font-medium hover:bg-blue-50 transition"
                >
                  Try Example
                </button>
              </div>
            </div>
          </div>
        </motion.section>
      </div>

      {/* Footer below hero block */}
      <Footer />
    </>
  );
}

export default Hero;
